import React, { useEffect, useState } from 'react';
import ProgressBar from '@ramsey-design-system/progress-bar';
import styles from './lesson-display.module.css';
import getNavigationMap from '../../lib/getNavigationMap';
import mergeActionItems from '../../lib/mergeActionItems';
import ActionItemContainer from './ActionItemContainer';
import { ActionGroup, ActionItem, UserData } from '../../types';
import cloneDeep from 'lodash/cloneDeep';
import {
  LessonVideoTemplate,
  ResponseQuestionTemplate,
  StaticContentTemplate,
  SurveyTemplate,
} from './templates';
import LessonOutline from '../LessonOutline/LessonOutline';
import { saveProgress } from '../../lib/learnClient';
import { CoverageCheckupTemplate } from './templates/CoverageCheckupTemplate/CoverageCheckupTemplate';
import { formatAutoFillableFields } from './templates/CoverageCheckupTemplate/utils/formatAutoFillableFields';

type SurveyConfig = {
  tokenProvider: () => Promise<Response>;
  customErrorMessage?: string;
  customLoadingMessage?: string;
};

type LessonDisplayProps = {
  actionItem: ActionItem;
  actionGroups: ActionGroup[];
  completionUrl: string;
  title: string;
  subtitle?: string;
  urlPrefix?: string;
  surveyConfig: SurveyConfig;
  collapsedActionGroupId?: string;
  collapsedProgressMode?: 'bar' | 'text';
  userData?: UserData;
};

function TemplateFactory(props: {
  actionItem: ActionItem;
  surveyConfig: SurveyConfig;
  onUpdate: any;
  urlPrefix: string;
  userData?: UserData;
}) {
  const { actionItem, surveyConfig, urlPrefix, onUpdate, userData } = props;

  switch (actionItem.template) {
    case 'static-content':
      return <StaticContentTemplate actionItem={actionItem} />;

    case 'lesson-video':
      return (
        <LessonVideoTemplate
          actionItem={actionItem}
          onComplete={onUpdate}
          customProps={{
            cast: {},
            horizontalVolumeSlider: true,
            playbackRateControls: true,
          }}
        />
      );

    case 'response-question':
      return (
        <ResponseQuestionTemplate actionItem={actionItem} onUpdate={onUpdate} />
      );

    case 'survey':
      if (
        actionItem.body?.survey_type &&
        actionItem.body.survey_type === 'trusted-coverage'
      ) {
        return (
          <CoverageCheckupTemplate
            actionItem={actionItem}
            completeFpuActionItem={onUpdate}
            autoFillableFields={formatAutoFillableFields(userData)}
          />
        );
      }
      return (
        <SurveyTemplate
          config={surveyConfig}
          actionItem={actionItem}
          urlPrefix={urlPrefix}
          completionCallback={onUpdate}
        />
      );

    default:
      throw new Error(`Invalid template type: ${actionItem.template}`);
  }
}

/**
 * Must be wrapped with a React BrowserRouter in the client application
 * @param param0
 * @returns
 */
function LessonDisplay({
  actionItem,
  actionGroups,
  completionUrl,
  surveyConfig,
  title,
  subtitle,
  urlPrefix = '/learn',
  collapsedActionGroupId = 'multi-survey',
  collapsedProgressMode = 'text',
  userData,
}: LessonDisplayProps) {
  const [displayState, setDisplay] = useState({ actionGroups, actionItem });
  const actionItems = mergeActionItems(displayState.actionGroups);
  const navigationMap = getNavigationMap(actionItems, completionUrl, urlPrefix);
  const navigation = navigationMap[actionItem.id];

  useEffect(() => {
    setDisplay({ actionGroups, actionItem });
  }, [actionGroups, actionItem]);

  const isAllActionItemsComplete = () => {
    for (const actionItem of actionItems) {
      if (actionItem.optional) {
        continue;
      }
      if (!actionItem.complete) {
        return false;
      }
    }
    return true;
  };

  /**
   * Sets the lesson complete and returns the response from the server (or false if the lesson is not complete).
   *
   * @param actionItem
   * @returns
   */
  const possiblySetLessonComplete = (actionItem: ActionItem): Promise<any> => {
    if (isAllActionItemsComplete()) {
      return saveProgress({
        course_id: actionItem.course_id,
        lesson_id: actionItem.lesson_id,
        data: { complete: true },
        template: actionItem.template,
      });
    } else {
      return Promise.resolve(false);
    }
  };

  const updateLessonDisplay = (actionItem: ActionItem): Promise<any> => {
    const actionItemId = actionItem.id;

    let actionGroupIndex = 0;
    let actionItemIndex = 0;

    for (let group = 0; group < actionGroups.length; group++) {
      const actionItems = actionGroups[group].action_items;

      for (let item = 0; item < actionItems.length; item++) {
        if (actionItems[item].id === actionItemId) {
          actionGroupIndex = group;
          actionItemIndex = item;
        }
      }
    }

    const updatedCurrentActionItem = { ...actionItem, complete: true };
    const displayCopy = cloneDeep(actionGroups);
    displayCopy[actionGroupIndex].action_items[
      actionItemIndex
    ] = updatedCurrentActionItem;

    const actionItemsCopy = cloneDeep(actionItem);
    actionItemsCopy.complete = true;

    setDisplay({ actionGroups: displayCopy, actionItem: actionItemsCopy });

    // Mark lesson complete if all of the action items are complete.
    return possiblySetLessonComplete(actionItem);
  };

  const renderProgressForCollapsedItem = (
    actionItem: ActionItem,
    actionGroups: ActionGroup[]
  ) => {
    if (collapsedActionGroupId === actionItem.action_group_id) {
      // Get the action items and determine the progress bar based on position in the array
      const actionGroupArray = actionGroups.filter(
        possibleGroup => collapsedActionGroupId === possibleGroup.id
      );
      if (actionGroupArray.length === 0) {
        // Shouldn't happen
        return null;
      }

      const actionGroup = actionGroupArray[0];
      const totalItems = actionGroup.action_items.length + 1;
      let currentItemIndex = 1;
      for (const currentItem of actionGroup.action_items) {
        if (actionItem.id === currentItem.id) {
          break;
        }
        currentItemIndex++;
      }
      const progress = (currentItemIndex / totalItems) * 100;

      const progressViewer =
        collapsedProgressMode === 'bar' ? (
          <div className={styles.progress_bar_wrapper}>
            <ProgressBar value={progress} size="large" />
          </div>
        ) : (
          <div className={styles.progress_text_wrapper}>
            <span className={styles.progress_text}>
              {currentItemIndex - 1} of {totalItems - 1} Completed
            </span>
          </div>
        );

      // Progress bar value is the position of the action item in the group
      return <>{progressViewer}</>;
    } else {
      return null;
    }
  };

  /**
   * Hides the survey progress bar for the collapsed items
   *
   * @param actionItem
   * @returns
   */
  const getWrapperClassNames = (actionItem: ActionItem) => {
    return collapsedActionGroupId === actionItem.action_group_id
      ? `${styles.container} ${styles.no_progress_bar}`
      : styles.container;
  };

  return (
    <div className={getWrapperClassNames(displayState.actionItem)}>
      <ActionItemContainer
        actionItem={displayState.actionItem}
        navigation={navigation}
        onUpdate={updateLessonDisplay}
        urlPrefix={urlPrefix}
      >
        {renderProgressForCollapsedItem(
          displayState.actionItem,
          displayState.actionGroups
        )}
        <TemplateFactory
          actionItem={displayState.actionItem}
          surveyConfig={surveyConfig}
          onUpdate={updateLessonDisplay}
          urlPrefix={urlPrefix}
          userData={userData}
        />
      </ActionItemContainer>

      {actionItems.length > 1 && (
        <LessonOutline
          actionGroups={displayState.actionGroups}
          compact
          title={title}
          urlPrefix={urlPrefix}
          lessonNumber={subtitle}
          currentActionItemId={actionItem.id}
        />
      )}
    </div>
  );
}

export default LessonDisplay;
export { LessonDisplayProps, SurveyConfig };
